import type React from "react"
import { useEffect } from "react"
import {
    addOrientationChangeListener,
    removeOrientationChangeListener,
} from "@multimediallc/web-utils"
import styles from "./ConfirmModal.module.scss"

export enum Alignment {
    left = "left",
    center = "center",
    right = "right",
}

interface ConfirmModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title?: string | React.ReactNode
    message: string | React.ReactNode
    confirmText: string
    cancelText?: string | undefined
    confirmButtonStyle?: {
        backgroundColor?: string
        color?: string
    }
    cancelButtonStyle?: {
        backgroundColor?: string
        color?: string
        borderColor?: string
    }
    hideOverlayOnClick?: boolean
    preventCancel?: boolean
    rotateDismiss?: boolean
    confirmButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
    cancelButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
    textAlign?: Alignment
    linkText?: string
    linkUrl?: string
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    confirmButtonStyle,
    cancelButtonStyle,
    hideOverlayOnClick = true,
    preventCancel = false,
    rotateDismiss = false,
    confirmButtonProps,
    cancelButtonProps,
    textAlign = Alignment.center,
    linkText,
    linkUrl,
}: ConfirmModalProps) {
    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                onConfirm()
            }
            if (event.key === "Escape" && !preventCancel) {
                onClose()
            }
        }

        const handleOrientationChange = () => {
            onClose()
        }

        window.addEventListener("keydown", handleKeyDown)
        if (rotateDismiss) {
            addOrientationChangeListener(handleOrientationChange)
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown)
            removeOrientationChangeListener(handleOrientationChange)
        }
    }, [isOpen, onConfirm, onClose])

    if (!isOpen) return null

    const handleOverlayClick = () => {
        if (hideOverlayOnClick) {
            onClose()
        }
    }

    const handleContentClick = (e: React.MouseEvent) => {
        e.stopPropagation()
    }

    const TEXT_ALIGN_CLASS_MAP = {
        center: styles.textAlignCenter,
        left: styles.textAlignLeft,
        right: styles.textAlignRight,
    }

    const textAlignClass = TEXT_ALIGN_CLASS_MAP[textAlign]
    return (
        <div
            data-testid="modal-overlay"
            className={styles.modalOverlay}
            onClick={handleOverlayClick}
        >
            <div
                data-testid="modal-content"
                className={styles.modalContent}
                onClick={handleContentClick}
            >
                {title !== undefined && (
                    <h1
                        data-testid="modal-title"
                        className={`${styles.modalTitle} ${textAlignClass}`}
                    >
                        {title}
                    </h1>
                )}
                <h2
                    data-testid="modal-message"
                    className={`${styles.modalMessage} ${textAlignClass}`}
                >
                    {message}
                </h2>
                {linkUrl && linkText && (
                    <p
                        data-testid="modal-link-paragraph"
                        className={`${styles.modalLinkParagraph} ${textAlignClass}`}
                    >
                        <a
                            data-testid="modal-link"
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {linkText}
                        </a>
                    </p>
                )}
                <div className={styles.modalButtons}>
                    {cancelText !== undefined && (
                        <button
                            data-testid="modal-cancel-button"
                            className={styles.modalButtonCancel}
                            onClick={onClose}
                            style={
                                {
                                    backgroundColor:
                                        cancelButtonStyle?.backgroundColor,
                                    color: cancelButtonStyle?.color,
                                    borderColor: cancelButtonStyle?.borderColor,
                                } as React.CSSProperties
                            }
                            {...cancelButtonProps}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        data-testid="modal-confirm-button"
                        className={styles.modalButtonDelete}
                        onClick={onConfirm}
                        style={
                            {
                                backgroundColor:
                                    confirmButtonStyle?.backgroundColor,
                                color: confirmButtonStyle?.color,
                            } as React.CSSProperties
                        }
                        {...confirmButtonProps}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    )
}
